import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useInsertionEffect, useLayoutEffect } from 'react';
import type { DisplayId } from './utils/toDisplayId';

const visibleItemIdsAtom = atom(new Set<DisplayId>());

export const useVisibleMagneticWallItemIds = () =>
  useAtomValue(visibleItemIdsAtom);

export const useIsMagneticWallItemVisible = () => {
  return useAtomCallback(
    useCallback(
      (get, _set, id: DisplayId) => get(visibleItemIdsAtom).has(id),
      [],
    ),
  );
};

export const MAGNETIC_WALL_ITEMS_DEFAULT_STYLES = `
  [class*='annotation-'] {
    --color-highlight-default: var(--color-editor-annotation-bg-default);
    --color-highlight-emphasis: var(--color-editor-annotation-bg-emphasis);
  }

  [class*='comment-'] {
    --color-highlight-default: var(--color-editor-comment-bg-default);
    --color-highlight-emphasis: var(--color-editor-comment-bg-emphasis);
  }

  [class*='reaction-'] {
    --color-highlight-default: var(--color-editor-reaction-bg-default);
    --color-highlight-emphasis: var(--color-editor-reaction-bg-emphasis);
  }
`;

export function getCssForHiddenItems(ids: DisplayId[]) {
  const selector = (['comment', 'reaction', 'annotation'] as const)
    .map((type) => {
      const idsForType = ids.filter((id) => id.startsWith(`${type}-`));
      if (idsForType.length === 0) return `[class*='${type}']`;

      const classList = idsForType.map((id) => `.${id}`).join(',');
      return `[class*='${type}']:not(${classList})`;
    })
    .join(',');

  if (selector === '') return '';

  return `${selector} {
    --color-highlight-default: transparent;
    --color-highlight-emphasis: transparent;
  }`;
}

const useVisibleMagneticWallItems = (displayIds: Set<DisplayId>) => {
  const setVisibleItemIds = useSetAtom(visibleItemIdsAtom);

  const stringifiedDisplayIdArray = JSON.stringify(
    Array.from(displayIds).toSorted(),
  );

  useLayoutEffect(() => {
    setVisibleItemIds(new Set(JSON.parse(stringifiedDisplayIdArray)));
  }, [setVisibleItemIds, stringifiedDisplayIdArray]);

  useInsertionEffect(() => {
    const displayIdArray = JSON.parse(stringifiedDisplayIdArray) as DisplayId[];
    if (!displayIdArray.length) return undefined;

    const styleTag = document.createElement('style');
    document.head.appendChild(styleTag);

    styleTag.textContent = `
      ${MAGNETIC_WALL_ITEMS_DEFAULT_STYLES}
      ${getCssForHiddenItems(displayIdArray)}
    `;

    return () => {
      styleTag.remove();
    };
  }, [stringifiedDisplayIdArray]);
};

export default useVisibleMagneticWallItems;
